import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The spoken walkthrough.
 *
 * Two faults in the document this replaces, both fixed here:
 *
 *   PAUSE DID NOTHING. The old player kept a timer that fired regardless, so
 *   pressing pause stopped the voice and the slides carried on without it. Here a
 *   single `playingRef` is the only thing that decides whether the walkthrough
 *   advances, it is read at the moment each line ends, and pausing cancels the
 *   utterance AND the safety timer. Nothing moves while it is false.
 *
 *   SPEED SKIPPED AHEAD. At 1.25x the voice took longer than the fixed delay that
 *   was meant to cover it, so the next step began mid-sentence. Here NOTHING is on
 *   a fixed delay: the next line starts when the voice reports it has finished.
 *   The only timer is a safety net for devices whose voice never reports an end,
 *   and it is scaled by the rate, so a faster rate cannot outrun it.
 *
 * Browsers also stop speaking after ~15s on some platforms unless nudged, hence
 * the resume tick while speaking.
 */
// Offered speeds. They are the REAL rate, not a label over something else: 1.25x
// speaks at 1.25. The earlier mapping turned 1x into 0.85 and 1.25x into 1.0, which is
// why the buttons were indistinguishable. (user 2026-10-04)
const SPEEDS = [1, 1.25, 1.5];

export function useSopNarration({ lines, lang, onIndex }) {
    const [index, setIndex] = useState(-1);
    const [playing, setPlaying] = useState(false);
    // Only the offered speeds; somebody who chose 1.25x before it existed as an option
    // would otherwise sit on a setting no button can show.
    const [rate, setRate] = useState(() => {
        try {
            const r = parseFloat(localStorage.getItem('sop-speed'));
            return SPEEDS.includes(r) ? r : 1;
        } catch { return 1; }
    });
    const [voiceOn, setVoiceOn] = useState(true);
    const [noVoice, setNoVoice] = useState(false);

    // The one source of truth for "should the walkthrough move on?", read at the
    // moment a line ends rather than captured when it started.
    const playingRef = useRef(false);
    const timerRef = useRef(null);
    const nudgeRef = useRef(null);
    const indexRef = useRef(-1);
    // Which line the player is on, counted. speak() cancels whatever is talking, and a
    // cancel makes the browser fire that utterance's onend - so changing speed mid-line
    // ended the old line, which advanced, which cancelled again, and the walkthrough
    // sprinted to the last line in a second and showed "Done".  A line may only finish
    // itself while it is still the current one.
    // (user 2026-10-04: "put speed, then it show resume. suppose its ongoing")
    const genRef = useRef(0);

    // The live speed and voice setting. speak() used to close over the state, so the
    // chain of lines already in flight kept the values it started with: pressing 1.5x
    // changed nothing until the walkthrough was restarted, and the Voice switch did
    // nothing at all once it was running. A ref is read at the moment each line is
    // spoken. (user 2026-10-04: "the speed doesnt really change" / "turn on and turn
    // off voice no different")
    const rateRef = useRef(rate);
    const voiceOnRef = useRef(voiceOn);
    useEffect(() => { rateRef.current = rate; }, [rate]);
    useEffect(() => { voiceOnRef.current = voiceOn; }, [voiceOn]);

    // The number on the button IS the rate. This used to map 1x to 0.85 and 1.25x to
    // 1.0, copying the document, which squeezed the three settings into a range too
    // narrow to hear - "i cant feel diff". Browsers accept 0.1 to 10, so 1 / 1.5 / 2
    // all pass straight through. (user 2026-10-04)
    const voiceRate = (r) => Math.min(10, Math.max(0.1, Number(r) || 1));

    const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;

    const clearTimers = useCallback(() => {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        if (nudgeRef.current) { clearInterval(nudgeRef.current); nudgeRef.current = null; }
    }, []);

    const stopVoice = useCallback(() => {
        genRef.current += 1;        // nothing in flight may finish and advance past this
        clearTimers();
        try { synth?.cancel(); } catch { /* nothing speaking */ }
    }, [clearTimers, synth]);

    const pickVoice = useCallback(() => {
        if (!synth) return null;
        const prefs = { en: ['en-GB', 'en-US', 'en'], zh: ['zh-CN', 'zh-SG', 'zh-TW', 'zh', 'cmn'], ms: ['ms-MY', 'ms', 'id-ID', 'id'] };
        const voices = synth.getVoices() || [];
        for (const p of prefs[lang] || prefs.en) {
            const v = voices.find(x => x.lang.replace('_', '-').toLowerCase().startsWith(p.toLowerCase()));
            if (v) return v;
        }
        return null;
    }, [synth, lang]);

    /** Speak one line, then call done() — but only once, however the end arrives. */
    const speak = useCallback((text, done) => {
        clearTimers();
        const mine = ++genRef.current;
        let finished = false;
        const finish = () => {
            if (finished || mine !== genRef.current) return;   // superseded: not ours to end
            finished = true;
            done();
        };

        const canSpeak = voiceOnRef.current && synth && typeof window.SpeechSynthesisUtterance === 'function';
        if (!canSpeak) {
            // Reading pace, scaled by the chosen speed so the captions keep up with it.
            timerRef.current = setTimeout(finish, (1400 + text.length * 55) / rateRef.current);
            return;
        }

        const paced = () => {
            // Reading pace, scaled by the chosen speed, used whenever the voice cannot be.
            clearTimers();
            timerRef.current = setTimeout(finish, (1400 + text.length * 55) / rateRef.current);
        };

        const v = pickVoice();
        if (!v && (synth.getVoices() || []).length > 0) {
            setNoVoice(true);
            paced();
            return;
        }
        setNoVoice(false);

        try { synth.cancel(); } catch { /* nothing to cancel */ }
        const u = new window.SpeechSynthesisUtterance(text);
        if (v) u.voice = v;
        u.lang = v ? v.lang : { en: 'en-GB', zh: 'zh-CN', ms: 'ms-MY' }[lang] || 'en-GB';
        u.rate = voiceRate(rateRef.current);

        // An utterance that never opened its mouth has not finished - it failed, and
        // treating that as "line done" marched the walkthrough to the last line in a
        // couple of seconds and left it reading "Done". It happens for real: a browser
        // with no installed voice for the language errors immediately, and so does every
        // utterance cancelled by the next one. Only an error AFTER speech began is an end;
        // before that, fall back to reading pace so the captions still work.
        // (user 2026-10-04: "put speed, then it show resume. suppose its ongoing")
        let began = false;
        u.onstart = () => { began = true; setNoVoice(false); };
        u.onend = finish;
        u.onerror = () => {
            if (began) finish();
            else { setNoVoice(true); paced(); }
        };

        // Only a safety net, and generous: scaled by the rate so a faster voice can
        // never be cut off by it. The real signal is onend.
        timerRef.current = setTimeout(finish, (5000 + text.length * 140) / rateRef.current);

        // Some browsers stop speaking after ~15s unless nudged.
        nudgeRef.current = setInterval(() => {
            try { if (synth.speaking && !synth.paused) { synth.pause(); synth.resume(); } } catch { /* ignore */ }
        }, 10000);

        synth.speak(u);
    }, [synth, pickVoice, lang, clearTimers]);   // no rate / voiceOn: both are read from refs

    const goTo = useCallback((i) => {
        if (!lines.length) return;
        const next = Math.max(0, Math.min(lines.length - 1, i));
        indexRef.current = next;
        setIndex(next);
        onIndex?.(next);

        const text = lines[next]?.[1] || '';
        speak(text, () => {
            // Read NOW, not when this line started — so a pause part-way through
            // actually stops the walkthrough here.
            if (!playingRef.current) return;
            if (indexRef.current < lines.length - 1) goTo(indexRef.current + 1);
            else { playingRef.current = false; setPlaying(false); }
        });
    }, [lines, speak, onIndex]);

    const play = useCallback(() => {
        if (!lines.length) return;
        playingRef.current = true;
        setPlaying(true);
        const from = indexRef.current >= 0 && indexRef.current < lines.length - 1 ? indexRef.current : 0;
        goTo(from);
    }, [lines, goTo]);

    const pause = useCallback(() => {
        playingRef.current = false;
        setPlaying(false);
        stopVoice();
    }, [stopVoice]);

    const toggle = useCallback(() => { if (playingRef.current) pause(); else play(); }, [play, pause]);

    const step = useCallback((delta) => {
        pause();
        goTo((indexRef.current < 0 ? 0 : indexRef.current) + delta);
    }, [pause, goTo]);

    const reset = useCallback(() => {
        playingRef.current = false;
        setPlaying(false);
        stopVoice();
        indexRef.current = -1;
        setIndex(-1);
    }, [stopVoice]);

    const changeRate = useCallback((r) => {
        setRate(r);
        rateRef.current = r;
        try { localStorage.setItem('sop-speed', String(r)); } catch { /* private mode */ }
        // Heard straight away, on the line being spoken - a speed control that only takes
        // effect on the next sentence reads as a speed control that does nothing. Safe to
        // re-speak now: the generation counter stops the old line finishing and advancing.
        if (playingRef.current && indexRef.current >= 0) goTo(indexRef.current);
    }, [goTo]);

    const changeVoiceOn = useCallback((on) => {
        setVoiceOn(on);
        voiceOnRef.current = on;
        if (playingRef.current && indexRef.current >= 0) goTo(indexRef.current);
        else if (!on) stopVoice();
    }, [goTo, stopVoice]);

    // Leaving the page must not leave a voice talking to an empty room.
    useEffect(() => () => { playingRef.current = false; stopVoice(); }, [stopVoice]);

    // Ask for the voice list early; some browsers fill it asynchronously.
    useEffect(() => { try { synth?.getVoices(); } catch { /* ignore */ } }, [synth]);

    return {
        index, playing, rate, voiceOn, noVoice,
        total: lines.length,
        play, pause, toggle, next: () => step(1), prev: () => step(-1),
        reset, setRate: changeRate, setVoiceOn: changeVoiceOn,
    };
}
